import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Aceptación de los Términos',
    body: 'Al adquirir el Protocolo Cortisol Off, usted acepta quedar vinculado por estos Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no podrá acceder al producto.'
  }, {
    heading: '2. Descripción del Producto',
    body: 'El Protocolo Cortisol Off es un producto digital educativo diseñado para ayudar a profesionales y emprendedores a gestionar el estrés y mejorar su rendimiento cognitivo. El producto incluye un manual digital, guías y materiales de apoyo en formato descargable.'
  }, {
    heading: '3. Uso Personal',
    body: 'Este producto está licenciado para uso personal únicamente. Queda estrictamente prohibida la reproducción, distribución, reventa o transmisión del contenido a terceros sin autorización expresa por escrito.'
  }, {
    heading: '4. Garantía de Devolución',
    body: 'Ofrecemos una garantía de satisfacción de 30 días. Si dentro de los primeros 30 días desde la compra no está satisfecho con el producto, puede solicitar un reembolso completo sin necesidad de justificación.'
  }, {
    heading: '5. Descargo de Responsabilidad Médica',
    body: 'El contenido de este producto es de carácter informativo y educativo. No constituye asesoramiento médico, diagnóstico ni tratamiento. Siempre consulte a un profesional de la salud calificado antes de realizar cambios significativos en su rutina de salud o bienestar.'
  }, {
    heading: '6. Propiedad Intelectual',
    body: 'Todo el contenido incluido en el Protocolo Cortisol Off, incluyendo textos, gráficos, logotipos y materiales audiovisuales, es propiedad exclusiva de sus creadores y está protegido por las leyes de derechos de autor aplicables.'
  }, {
    heading: '7. Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación. El uso continuado del producto implica la aceptación de los términos modificados.'
  }, {
    heading: '8. Ley Aplicable',
    body: 'Estos términos se regirán e interpretarán de conformidad con las leyes aplicables, sin dar efecto a ningún principio de conflicto de leyes.'
  }]
};
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que Recopilamos',
    body: 'Recopilamos únicamente la información necesaria para procesar su compra y entregar el producto digital. Esto incluye su nombre, dirección de correo electrónico y datos de pago procesados de forma segura a través de nuestro proveedor de pagos.'
  }, {
    heading: '2. Uso de la Información',
    body: 'La información recopilada se utiliza exclusivamente para: (a) procesar y confirmar su compra, (b) entregar el producto digital adquirido, (c) enviar comunicaciones relacionadas con el producto, y (d) mejorar nuestros servicios y productos.'
  }, {
    heading: '3. Protección de Datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. Los datos de pago son procesados mediante cifrado SSL y nunca se almacenan en nuestros servidores.'
  }, {
    heading: '4. Compartición de Datos',
    body: 'No vendemos, intercambiamos ni transferimos su información personal a terceros, excepto a los proveedores de servicios de confianza que nos asisten en la operación de nuestro negocio (procesadores de pago, plataformas de entrega de contenido digital), quienes están obligados a mantener la confidencialidad de su información.'
  }, {
    heading: '5. Cookies',
    body: 'Nuestro sitio puede utilizar cookies para mejorar su experiencia de navegación. Estas cookies no recopilan información personal identificable y pueden ser desactivadas desde la configuración de su navegador.'
  }, {
    heading: '6. Sus Derechos',
    body: 'Usted tiene derecho a acceder, corregir o solicitar la eliminación de su información personal en cualquier momento. También puede optar por no recibir comunicaciones de marketing en cualquier momento utilizando el enlace de cancelación de suscripción incluido en nuestros correos electrónicos.'
  }, {
    heading: '7. Retención de Datos',
    body: 'Conservamos su información personal durante el tiempo necesario para cumplir con los fines descritos en esta política, a menos que la ley exija o permita un período de retención más largo.'
  }, {
    heading: '8. Cambios a Esta Política',
    body: 'Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos sobre cambios significativos publicando la nueva política en esta página. Le recomendamos revisar esta política periódicamente.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const content = type === 'terms' ? termsContent : privacyContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-10">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-brand-charcoal-dark/80 backdrop-blur-sm" data-id="element-11" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      y: 24,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 16,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1]
    }} className="relative bg-brand-cream w-full max-w-2xl max-h-[85vh] rounded-xl shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-12">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-brand-gold/20 bg-white flex-shrink-0" data-id="element-13">
          <h2 className="font-serif text-2xl font-bold text-brand-charcoal-dark" data-id="element-14">
            {content.title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full text-brand-charcoal/50 hover:text-brand-charcoal hover:bg-brand-cream-dark transition-colors" aria-label="Cerrar" data-id="element-15">
            <X className="h-5 w-5" data-id="element-16" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto px-8 py-6 space-y-6" data-id="element-17">
          {content.sections.map((section, i) => <div key={i} data-id="element-18">
              <h3 className="font-serif font-semibold text-brand-charcoal-dark mb-2" data-id="element-19">
                {section.heading}
              </h3>
              <p className="text-brand-charcoal/70 text-sm leading-relaxed" data-id="element-20">
                {section.body}
              </p>
            </div>)}

          <p className="text-xs text-brand-charcoal/40 pt-4 border-t border-brand-gold/10" data-id="element-21">
            Última actualización: {new Date().getFullYear()}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [openModal, setOpenModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brand-charcoal-dark text-white py-12 border-t border-brand-gold/20" data-id="element-22">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-23">
          <div className="text-center md:text-left" data-id="element-24">
            <h3 className="font-serif text-2xl font-bold text-brand-gold mb-2" data-id="element-25">
              Cortisol Off
            </h3>
            <p className="text-brand-cream/60 text-sm" data-id="element-26">
              © {new Date().getFullYear()} Protocolo Cortisol Off. Todos los
              derechos reservados.
            </p>
            <p className="text-brand-cream/30 text-xs mt-1" data-id="element-27">
              Este producto no sustituye el consejo médico profesional.
            </p>
          </div>

          <div className="flex gap-6 text-sm text-brand-cream/60" data-id="element-28">
            <button onClick={() => setOpenModal('terms')} className="hover:text-brand-gold transition-colors cursor-pointer" data-id="element-29">
              Términos y Condiciones
            </button>
            <button onClick={() => setOpenModal('privacy')} className="hover:text-brand-gold transition-colors cursor-pointer" data-id="element-30">
              Política de Privacidad
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-31">
        {openModal && <LegalModal type={openModal} onClose={() => setOpenModal(null)} data-id="element-32" />}
      </AnimatePresence>
    </>;
}